'use client';

import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
  getFilteredRowModel,
  Row,
} from '@tanstack/react-table';

import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { AnimationShell, Icon, Link, Overlay, Typography } from '@/components';
import { t } from 'i18next';
import { cn } from '@/utils/styles';
import { DataTablePagination } from './transaction-pagination';
import { Fragment, useState } from 'react';
import { TableFilterDropdown } from './transaction-filter';
import { useCountries, useTechnologies } from '@/hooks';
import { SearchFilterTable } from '../../../../components/search/search';
import { useCompanyRole } from '@/features/company/hooks';
import DOMPurify from 'dompurify';
import { getWidthClass } from '../../../../helpers/table-columns-width';

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  expandedRowId?: string | null;
  toggleRow: (id: string) => void;
}

export function TransactionsTable<TData, TValue>({
  columns,
  data,
  expandedRowId = null,
  toggleRow,
}: DataTableProps<TData, TValue>) {
  const [globalFilter, setGlobalFilter] = useState<string>('');
  const { filters: technologies } = useTechnologies();
  const { options: countries } = useCountries();

  const table = useReactTable({
    data,
    columns,
    state: {
      globalFilter,
    },
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    globalFilterFn: (row, columnId, filterValue) =>
      customGlobalFilterFn(row, ['date', 'buyer', 'seller', 'capacity', 'city'], filterValue),
    onGlobalFilterChange: setGlobalFilter,
  });

  function customGlobalFilterFn<TData>(row: Row<TData>, columnIds: string[], filterValue: string) {
    const searchValue = filterValue.toLowerCase().trim();
    return columnIds.some((id) => {
      const value = row.getValue(id);
      return String(value).toLowerCase().includes(searchValue);
    });
  }
  const { isPremium } = useCompanyRole();

  return (
    <>
      {isPremium && (
        <div className="flex flex-col gap-3 sm:flex-row sm:justify-between">
          <div className="flex w-full gap-2 sm:w-auto [&>*]:flex-1 sm:[&>*]:flex-none">
            <TableFilterDropdown
              column={table.getColumn('technologies')}
              title={t('Technologies')}
              options={technologies}
            />
            <TableFilterDropdown
              column={table.getColumn('country')}
              title={t('Country')}
              options={countries}
              marginRight="mr-0"
            />
          </div>
          <SearchFilterTable
            globalFilter={globalFilter}
            setGlobalFilter={setGlobalFilter}
            placeholder="Search"
            className="w-full sm:w-auto"
          />
        </div>
      )}
      <div className="grid place-items-stretch gap-6">
        <div className={cn(!isPremium && 'mt-8', 'bg-neutral-10 overflow-x-auto rounded-md border border-neutral-50')}>
          <Table className="w-full min-w-175 lg:min-w-0">
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id} className="hover:bg-muted border-neutral-50">
                  {headerGroup.headers.map((header) => {
                    const widthClass = getWidthClass('transactions', header.column.id);
                    return (
                      <TableHead key={header.id} className={`px-3 py-4 ${widthClass}`}>
                        <Typography
                          as="h6"
                          size="body-2"
                          weight="semibold"
                          className="whitespace-nowrap text-neutral-500"
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(header.column.columnDef.header, header.getContext())}
                        </Typography>
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody className="bg-white">
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <Fragment key={row.id}>
                    <TableRow
                      key={`${row.id}-main`}
                      data-state={row.getIsSelected() && 'selected'}
                      className={cn(
                        isPremium && 'cursor-pointer border-neutral-50 hover:bg-gray-200/50',
                        isPremium && expandedRowId === (row.original as any).id && 'border-b-0'
                      )}
                      onClick={() => isPremium && toggleRow((row.original as any).id)}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell className="truncate px-3 py-4" key={cell.id}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      ))}
                    </TableRow>
                    {isPremium && expandedRowId === (row.original as any).id && (
                      <TableRow
                        key={`${row.id}-expanded`}
                        className={cn('border-t-0 border-neutral-50 hover:bg-gray-200/50')}
                      >
                        <TableCell colSpan={table.getAllColumns().length} className="px-0">
                          <div className="space-y-1 px-5 py-3">
                            <Typography as="p" size="body-2" weight="medium">
                              {t('Transaction Details')}
                            </Typography>
                            {(row.original as any).details !== null ? (
                              <div
                                className="prose prose-base text-neutral-90 my-2 max-w-full text-base leading-relaxed font-normal break-words whitespace-normal"
                                dangerouslySetInnerHTML={{
                                  __html: DOMPurify.sanitize((row.original as any).details),
                                }}
                              />
                            ) : (
                              <Typography as="p" size="body-2" weight="normal" className="py-2">
                                No more info available
                              </Typography>
                            )}
                            {(row.original as any).link !== null ? (
                              <Typography as="span" size="body-2" weight="medium">
                                <Link
                                  to={(row.original as any).link}
                                  target="_blank"
                                  className="text-primary-700 inline-flex items-center gap-1 text-base font-medium"
                                >
                                  See more details
                                  <Icon name="link" className="inline-block h-4 w-4 align-middle" />
                                </Link>
                              </Typography>
                            ) : (
                              ''
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={columns.length} className="h-24 text-center">
                    No results.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          {!isPremium && <Overlay />}
        </div>
      </div>
      {isPremium && <DataTablePagination table={table} />}
    </>
  );
}
